// Visual building blocks for the HR admin dashboard. Reuses the manager design
// language (navy command bar, ManagerKpiTile) so the two surfaces read as one
// system, and adds the admin-only Stage Flow pipeline + inbox decision card.

import React from 'react';
import { Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  ArrowForwardRounded as ArrowForwardIcon,
  AttachFileRounded as AttachFileIcon,
  BeachAccessRounded as BeachIcon,
  CheckRounded as CheckIcon,
  CheckCircleRounded as CheckCircleIcon,
  ChevronRightRounded as ChevronRightIcon,
  CloseRounded as CloseIcon,
  DescriptionRounded as DescriptionIcon,
  FlightTakeoffRounded as FlightIcon,
  Refresh as RefreshIcon,
  VisibilityRounded as VisibilityIcon,
  WarningAmberRounded as WarningIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion, useReducedMotion } from 'framer-motion';

const KIND_VISUAL = {
  vacation: { tone: 'success', Icon: BeachIcon },
  exit_entry: { tone: 'info', Icon: FlightIcon },
  hr_request: { tone: 'secondary', Icon: DescriptionIcon },
};

const STAGE_LABELS = { manager: 'Manager', hr: 'HR review', finance: 'Finance' };

function getInitials(name) {
  return (
    String(name || '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase() || 'HR'
  );
}

export function ageLabel(hours) {
  const h = Number(hours) || 0;
  if (h < 24) return `${Math.max(0, Math.round(h))}h`;
  return `${Math.round(h / 24)}d`;
}

// The signature element: a Manager -> HR -> Finance conveyor showing where
// pending approvals are stuck. The fullest stage glows amber. Lives on navy.
export function StageFlow({ stages = {}, onStageClick }) {
  const order = ['manager', 'hr', 'finance'];
  const counts = order.map((key) => Number(stages?.[key]) || 0);
  const max = Math.max(...counts, 0);
  const total = counts.reduce((sum, n) => sum + n, 0);
  const bottleneckIndex = max > 0 ? counts.indexOf(max) : -1;

  return (
    <Box>
      <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.7, mb: 0.9 }}>
        Approvals pipeline
      </Typography>
      <Stack direction="row" spacing={0.75} alignItems="center">
        {order.map((key, index) => {
          const isBottleneck = index === bottleneckIndex;
          return (
            <React.Fragment key={key}>
              <Box
                component={onStageClick ? 'button' : 'div'}
                type={onStageClick ? 'button' : undefined}
                onClick={onStageClick ? () => onStageClick(key) : undefined}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'left',
                  font: 'inherit',
                  color: '#fff',
                  cursor: onStageClick ? 'pointer' : 'default',
                  p: '8px 10px',
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: isBottleneck ? 'rgba(250,199,117,0.7)' : 'rgba(255,255,255,0.18)',
                  backgroundColor: isBottleneck ? 'rgba(199,129,47,0.32)' : 'rgba(255,255,255,0.12)',
                }}
              >
                <Typography sx={{ fontSize: 11, opacity: 0.82 }}>{STAGE_LABELS[key]}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {counts[index]}
                </Typography>
              </Box>
              {index < order.length - 1 ? (
                <ChevronRightIcon aria-hidden sx={{ color: 'rgba(255,255,255,0.55)', flexShrink: 0 }} />
              ) : null}
            </React.Fragment>
          );
        })}
      </Stack>
      <Typography sx={{ fontSize: 12, opacity: 0.82, mt: 0.9 }}>
        {total === 0 ? (
          'Pipeline is clear.'
        ) : bottleneckIndex >= 0 ? (
          <>
            <WarningIcon aria-hidden sx={{ fontSize: 14, verticalAlign: '-2px', mr: 0.4 }} />
            Work is piling up at {STAGE_LABELS[order[bottleneckIndex]]}.
          </>
        ) : (
          'Pipeline is balanced.'
        )}
      </Typography>
    </Box>
  );
}

export function AdminCommandBar({
  adminName,
  role,
  dateLabel,
  headcount,
  pending,
  oldestLabel,
  stages,
  onRefresh,
  onReview,
  onStageClick,
  utility,
}) {
  const theme = useTheme();
  const reduced = useReducedMotion();
  const dark = theme.palette.mode === 'dark';
  const allClear = !pending;
  const firstName = String(adminName || '').trim().split(/\s+/)[0];

  return (
    <Box
      component={motion.div}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduced ? 0.01 : 0.45, ease: [0.2, 0, 0, 1] }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px',
        p: { xs: 1.6, sm: 2 },
        color: '#fff',
        border: '1px solid',
        borderColor: alpha('#ffffff', dark ? 0.16 : 0.22),
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${alpha(
          theme.palette.secondary.main,
          0.92
        )} 100%)`,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `radial-gradient(640px 200px at 12% -10%, ${alpha('#ffffff', 0.18)}, transparent 60%)`,
        }}
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ position: 'relative' }}>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              fontWeight: 700,
              backgroundColor: alpha('#ffffff', 0.18),
            }}
          >
            {getInitials(adminName)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.1 }} noWrap>
              {firstName ? `Good day, ${firstName}` : 'HR admin workspace'}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.8), fontVariantNumeric: 'tabular-nums' }} noWrap>
              {[role || 'HR admin', dateLabel, headcount ? `${headcount} active` : null].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} sx={{ color: '#fff', backgroundColor: alpha('#ffffff', 0.14), '&:hover': { backgroundColor: alpha('#ffffff', 0.24) } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {utility}
        </Stack>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.6, md: 3 }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        sx={{ position: 'relative', mt: { xs: 1.6, md: 1.5 } }}
      >
        <Stack direction="row" spacing={1.6} alignItems="center" sx={{ minWidth: 0, flexShrink: 0 }}>
          {allClear ? (
            <CheckCircleIcon sx={{ fontSize: 50, color: alpha('#ffffff', 0.92) }} />
          ) : (
            <Typography sx={{ fontSize: 50, fontWeight: 700, lineHeight: 0.9, fontVariantNumeric: 'tabular-nums' }}>
              {pending}
            </Typography>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.12 }}>
              {allClear ? 'Approval queue is clear' : `request${pending === 1 ? '' : 's'} await your decision`}
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.8), display: 'block', mb: 1 }}>
              {allClear ? 'Nothing is waiting on you right now.' : oldestLabel ? `Oldest has been waiting ${oldestLabel}` : 'Review and respond to keep requests moving.'}
            </Typography>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={onReview}
              sx={{ backgroundColor: '#fff', color: theme.palette.primary.dark, boxShadow: 'none', '&:hover': { backgroundColor: alpha('#ffffff', 0.88), boxShadow: 'none' } }}
            >
              {allClear ? 'Open inbox' : 'Review queue'}
            </Button>
          </Box>
        </Stack>

        <Box sx={{ minWidth: 0, flex: 1, width: '100%' }}>
          <StageFlow stages={stages} onStageClick={onStageClick} />
        </Box>
      </Stack>
    </Box>
  );
}

// One pending approval-inbox item (vacation / exit-entry / HR request) with
// inline approve / decline. Works across all three kinds.
export function InboxReviewCard({ item, busy = false, onApprove, onReject, onView }) {
  const theme = useTheme();
  const visual = KIND_VISUAL[item?.kind] || KIND_VISUAL.hr_request;
  const accent = theme.palette?.[visual.tone]?.main || theme.palette.primary.main;
  const Icon = visual.Icon;
  const actions = Array.isArray(item?.available_actions) ? item.available_actions : ['approve', 'reject'];
  const canApprove = actions.includes('approve');
  const canReject = actions.includes('reject');
  const urgency = item?.urgency;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.25,
        p: 1.1,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: theme.hr?.line?.soft || 'divider',
        backgroundColor:
          theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : alpha(theme.palette.background.paper, 0.96),
        opacity: busy ? 0.6 : 1,
        transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest }),
      }}
    >
      <Box
        aria-hidden
        sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: '11px', display: 'grid', placeItems: 'center', color: accent, backgroundColor: alpha(accent, theme.palette.mode === 'dark' ? 0.22 : 0.12) }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
              {item?.employee?.name || 'Unknown employee'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[item?.employee?.position || item?.employee?.department, `${item?.type_label || 'Request'} · ${item?.type_detail || ''}`.trim()].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          {urgency === 'overdue' ? (
            <Chip size="small" label="Overdue" sx={{ borderRadius: '8px', backgroundColor: alpha(theme.palette.error.main, 0.14), color: theme.palette.error.main, fontWeight: 600 }} />
          ) : urgency === 'due_today' ? (
            <Chip size="small" label="Due today" sx={{ borderRadius: '8px', backgroundColor: alpha(theme.palette.warning.main, 0.16), color: theme.palette.warning.dark, fontWeight: 600 }} />
          ) : null}
        </Stack>

        <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 0.9 }}>
          {item?.date_range?.label ? (
            <Chip size="small" variant="outlined" label={item.date_range.label} sx={{ borderRadius: '8px', '& .MuiChip-label': { fontWeight: 600, fontVariantNumeric: 'tabular-nums' } }} />
          ) : null}
          <Chip size="small" variant="outlined" label={`Waiting ${ageLabel(item?.submitted_age_hours)}`} sx={{ borderRadius: '8px', '& .MuiChip-label': { fontVariantNumeric: 'tabular-nums' } }} />
          {item?.has_attachment ? (
            <Chip size="small" variant="outlined" icon={<AttachFileIcon sx={{ fontSize: 15 }} />} label="Attachment" sx={{ borderRadius: '8px' }} />
          ) : null}
        </Stack>

        <Stack direction="row" spacing={0.8} sx={{ mt: 1.1 }} useFlexGap flexWrap="wrap">
          {canApprove ? (
            <Button size="small" variant="outlined" color="success" startIcon={<CheckIcon />} disabled={busy} onClick={() => onApprove?.(item)}>
              Approve
            </Button>
          ) : null}
          {canReject ? (
            <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon />} disabled={busy} onClick={() => onReject?.(item)}>
              Decline
            </Button>
          ) : null}
          {onView ? (
            <Button size="small" startIcon={<VisibilityIcon />} sx={{ ml: 'auto' }} onClick={() => onView(item)}>
              View
            </Button>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}
